/**
 * Google Analytics event tracking utility
 *
 * This module provides functions to send custom events to Google Analytics.
 * Events are sent using the global gtag function that is initialized by GoogleTag component.
 */

declare global {
	interface Window {
		gtag?: (
			command: string,
			eventName: string,
			eventParams?: Record<string, any>
		) => void;
		dataLayer?: any[];
	}
}

export type GAEventName =
	| "Login"
	| "Signup"
	| "FreeTrial"
	| "startjobtracking"
	| "subscribe"
	| "social";

/**
 * Send a custom event to Google Analytics
 * @param eventName - The name of the event to track
 * @param eventParams - Optional additional parameters to send with the event
 */
export const sendGAEvent = (
	eventName: GAEventName,
	eventParams?: Record<string, any>
): void => {
	if (typeof window !== "undefined" && window.gtag) {
		window.gtag("event", eventName, {
			...eventParams,
			timestamp: new Date().toISOString()
		});
		console.log(`GA Event: ${eventName}`, eventParams);
	} else {
		console.warn(
			"Google Analytics not initialized. Event not sent:",
			eventName
		);
	}
};

/**
 * Track a button click event
 * @param buttonName - The name/label of the button clicked
 * @param location - Where on the page the button is located (e.g., 'navbar', 'hero', 'pricing')
 */
export const trackButtonClick = (
	eventName: GAEventName,
	buttonName: string,
	location?: string
): void => {
	sendGAEvent(eventName, {
		button_name: buttonName,
		location: location || "unknown"
	});
};

/**
 * Track a social media link click
 * @param platform - The name of the social media platform (e.g., 'LinkedIn', 'Facebook', 'Instagram', 'X')
 * @param url - The URL being clicked
 */
export const trackSocialClick = (platform: string, url: string): void => {
	sendGAEvent("social", {
		platform: platform,
		url: url,
		location: "footer"
	});
};
